import { postApi } from "utils/apiCall";
import laundryAuditClient from "./laundryAuditClient";
import { CLIENT_ID } from "constants/ApiEndPointsConstants";

// module_id "19" matches the mapping in src/components/layout-components/MenuContent.js's
// handleExternalClick, which mints this same handoff token for the old
// window.open flow.
const LAUNDRY_AUDIT_MODULE_ID = "19";

// Same two-step handoff pattern as Cleaning Audit (see ../cleaningAudit/cleaningAuditAuth.js),
// with one extra wrinkle: Laundry Audit's own auth also requires a "user-id"
// header carrying its own user id (called "luser_id" in the old client), not
// just the token — confirmed by reading MajesticERP-LaundryAuditAPI/code/controllers/
// LaundryUsersController.js's LaundryAutoLogin handler directly (req.headers["user-id"]).
//
// 1. MyM's OWN backend mints a fresh, one-time, module-scoped token +
//    luser_id via /auto_login_app_web (src/redux/actions/postAutoLogin.action.js).
// 2. That handoff (token + luser_id) is exchanged with Laundry Audit's
//    /laundry_auto_login for a Laundry-Audit-specific session token + its own
//    user id, which every further call uses as token-id/user-id headers.
let cachedSessionPromise = null;
// Which MyM user/client the cached session belongs to. Sign-out/sign-in
// doesn't reload the page, so without this a manager signing in after an
// admin in the same tab would keep using the admin's module session.
let cachedSessionOwner = null;
const currentSessionOwner = () => `${localStorage.getItem("user_id")}|${localStorage.getItem("client_id")}`;

const getHandoff = async () => {
  const userId = localStorage.getItem("user_id");
  const { error, result } = await postApi("/auto_login_app_web", {
    module_id: LAUNDRY_AUDIT_MODULE_ID,
    user_id: userId,
  });
  const handoff = result?.result;
  if (error || !handoff?.token_id) {
    throw new Error("Could not obtain a Laundry Audit handoff token from MyM.");
  }
  return handoff; // { token_id, luser_id, user_id, ... }
};

const exchangeToken = async () => {
  const handoff = await getHandoff();
  const mymUserId = localStorage.getItem("user_id");
  const { data } = await laundryAuditClient.post(
    "/laundry_auto_login",
    { token_id: handoff.token_id, user_id: mymUserId },
    {
      headers: {
        "token-id": handoff.token_id,
        "user-id": handoff.luser_id,
        "device-type": process.env.REACT_APP_LA_DEVICE_TYPE,
      },
    }
  );
  const sessionTokenId = data?.result?.token_id;
  if (!sessionTokenId) {
    throw new Error("Laundry Audit login did not return a token.");
  }
  return {
    tokenId: sessionTokenId,
    userId: data?.result?.luser_id ?? handoff.luser_id,
    clientId: localStorage.getItem(CLIENT_ID),
  };
};

export const getLaundryAuditSession = () => {
  if (cachedSessionOwner !== currentSessionOwner()) {
    cachedSessionPromise = null;
    cachedSessionOwner = currentSessionOwner();
  }
  if (!cachedSessionPromise) {
    cachedSessionPromise = exchangeToken().catch((error) => {
      cachedSessionPromise = null; // let the next call retry instead of caching a failure
      throw error;
    });
  }
  return cachedSessionPromise;
};

// Same rationale as Cleaning Audit's invalidateCleaningAuditSession — force a
// fresh exchange after a 401 rather than assume the user is unauthorized.
export const invalidateLaundryAuditSession = () => {
  cachedSessionPromise = null;
};
